import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { pickLangText } from "@/i18n";
import CityPicker from "@/components/CityPicker";
import TimePicker from "@/components/TimePicker";
import LifeEvents from "@/components/LifeEvents";
import PhoneInput from "@/components/PhoneInput";

const Booking = ({ packages }) => {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const { state } = useLocation();

  const [selectedId, setSelectedId] = useState(
    state?.pkg?.id || packages?.[0]?.id || ""
  );
  const selected = useMemo(
    () => (packages || []).find((p) => p.id === selectedId),
    [packages, selectedId]
  );

  const [form, setForm] = useState({
    full_name: "",
    gender: "Male",
    contact: "+91",
    email: "",
    dob: "",
    time_of_birth: "",
    place_of_birth: "",
    questions: "",
    life_events: [], // array of { date, time, text }
    first_child_dob: "",
    first_child_tob: "",
    second_child_dob: "",
    second_child_tob: "",
  });
  const [err, setErr] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setErr(null);
    if (!selected) {
      setErr("Please select a package");
      return;
    }
    // navigate to payment carrying form + package
    nav("/payment", {
      state: {
        pkg: selected,
        form,
      },
    });
  };

  if (!packages || packages.length === 0) {
    return (
      <div className="section container-narrow">
        <div className="notice">Loading packages…</div>
      </div>
    );
  }

  return (
    <div
      className={`section ${lang === "hi" ? "font-devnagri" : ""}`}
      data-testid="booking-page"
    >
      <div className="container-narrow">
        <div className="text-center mb-10">
          <span className="gold-divider mb-4">Step 1 of 2</span>
          <h1 className="font-display text-4xl md:text-5xl gold-text mb-3">
            {t.bookingTitle}
          </h1>
          <p className="text-[color:var(--cream)] opacity-85 max-w-xl mx-auto">
            {t.bookingSub}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Package selector */}
          <div className="lg:col-span-1">
            <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--gold-soft)] mb-3">
              {t.selectedPackage}
            </div>
            <div className="space-y-3">
              {packages.map((p) => {
                const info = {
                  title: pickLangText(p, lang, "title"),
                  sub: pickLangText(p, lang, "subtitle"),
                };
                const active = selectedId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedId(p.id)}
                    className={`w-full text-left card-glass p-4 transition ${
                      active
                        ? "outline outline-2 outline-[color:var(--gold)]"
                        : ""
                    }`}
                    style={active ? { borderColor: "var(--gold)" } : {}}
                    data-testid={`select-pkg-${p.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div
                          className="font-display text-lg"
                          style={{ color: "#f6ecd6" }}
                        >
                          {info.title}
                        </div>
                        <div className="text-xs text-[color:var(--gold-soft)] font-sans-ui">
                          {info.sub}
                        </div>
                      </div>
                      <div className="price-tag">
                        <span className="rupee">₹</span>
                        <span className="amount" style={{ fontSize: "1.3rem" }}>
                          {p.amount}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={submit} className="card-glass p-6 md:p-8 space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div className="field">
                  <label>
                    {t.fullName} <span className="text-red-300">*</span>
                  </label>
                  <input
                    className="input"
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    required
                    data-testid="input-full-name"
                  />
                </div>
                <div className="field">
                  <label>
                    {t.gender} <span className="text-red-300">*</span>
                  </label>
                  <select
                    className="select"
                    value={form.gender}
                    onChange={(e) => update("gender", e.target.value)}
                    data-testid="input-gender"
                  >
                    <option value="Male">{t.male}</option>
                    <option value="Female">{t.female}</option>
                    <option value="Other">{t.other}</option>
                  </select>
                </div>
                <div className="field">
                  <label>
                    {t.contactNumber} <span className="text-red-300">*</span>
                  </label>
                  <PhoneInput
                    value={form.contact}
                    onChange={(v) => update("contact", v)}
                    testId="input-contact"
                    required
                  />
                </div>
                <div className="field">
                  <label>{t.email}</label>
                  <input
                    className="input"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="input-email"
                  />
                </div>
                <div className="field">
                  <label>
                    {t.dob} <span className="text-red-300">*</span>
                  </label>
                  <input
                    className="input"
                    type="date"
                    value={form.dob}
                    onChange={(e) => update("dob", e.target.value)}
                    required
                    data-testid="input-dob"
                  />
                </div>
                <div className="field">
                  <label>
                    {t.tob} <span className="text-red-300">*</span>
                  </label>
                  <TimePicker
                    value={form.time_of_birth}
                    onChange={(v) => update("time_of_birth", v)}
                    testId="input-tob"
                    required
                  />
                </div>
                <div className="field md:col-span-2">
                  <label>
                    {t.pob} <span className="text-red-300">*</span>
                  </label>
                  <CityPicker
                    value={form.place_of_birth}
                    onChange={(v) => update("place_of_birth", v)}
                    testId="input-pob"
                    placeholder={t.pob}
                    required
                  />
                </div>
                <div className="field md:col-span-2">
                  <label>{t.questions}</label>
                  <textarea
                    className="textarea"
                    value={form.questions}
                    onChange={(e) => update("questions", e.target.value)}
                    data-testid="input-questions"
                  />
                </div>
                <div className="field md:col-span-2">
                  <label>{t.lifeEvents}</label>
                  <LifeEvents
                    value={form.life_events}
                    onChange={(v) => update("life_events", v)}
                    testId="input-life-events"
                    labels={{
                      add: t.lifeEventsAdd,
                      empty: t.lifeEventsEmpty,
                      placeholder: t.lifeEventsPlaceholder,
                      timeLabel: t.lifeEventsTime,
                    }}
                  />
                </div>
                <div className="field">
                  <label>{t.firstChildDob}</label>
                  <input
                    className="input"
                    type="date"
                    value={form.first_child_dob}
                    onChange={(e) => update("first_child_dob", e.target.value)}
                    data-testid="input-first-child-dob"
                  />
                  <div className="mt-2 space-y-1">
                    <span className="text-[10px] uppercase tracking-widest text-[color:var(--gold-soft)] font-sans-ui">
                      {t.firstChildTob}
                    </span>
                    <TimePicker
                      value={form.first_child_tob}
                      onChange={(v) => update("first_child_tob", v)}
                      testId="input-first-child-tob"
                    />
                  </div>
                </div>
                <div className="field">
                  <label>{t.secondChildDob}</label>
                  <input
                    className="input"
                    type="date"
                    value={form.second_child_dob}
                    onChange={(e) => update("second_child_dob", e.target.value)}
                    data-testid="input-second-child-dob"
                  />
                  <div className="mt-2 space-y-1">
                    <span className="text-[10px] uppercase tracking-widest text-[color:var(--gold-soft)] font-sans-ui">
                      {t.secondChildTob}
                    </span>
                    <TimePicker
                      value={form.second_child_tob}
                      onChange={(v) => update("second_child_tob", v)}
                      testId="input-second-child-tob"
                    />
                  </div>
                </div>
              </div>

              {err && <div className="notice error">{err}</div>}

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2 border-t border-[rgba(212,166,74,0.25)]">
                <div className="text-[color:var(--cream)] opacity-90">
                  {selected && (
                    <>
                      <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--gold-soft)] mb-1">
                        {t.selectedPackage}
                      </div>
                      <div className="font-display text-lg">
                        {pickLangText(selected, lang, "title")} · ₹{selected.amount}
                      </div>
                    </>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn-gold"
                  data-testid="proceed-to-pay-btn"
                >
                  {t.proceedToPay} <ChevronRight size={16} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
